/** How closely a record is held. Mirrors backend `src/security/confidentiality.ts`. */
export type Confidentiality = 'normal' | 'restricted' | 'secret';

export type GrantEntityType =
  | 'stakeholder'
  | 'organization'
  | 'wiki_page'
  | 'issue'
  | 'issue_stance';

export type UserRole = 'super_admin' | 'admin' | 'top_owner' | 'user';

export const CONFIDENTIALITY_LEVELS: Confidentiality[] = [
  'normal',
  'restricted',
  'secret',
];

export const CONFIDENTIALITY_LABELS: Record<Confidentiality, string> = {
  normal: 'Normal',
  restricted: 'Restricted',
  secret: 'Secret',
};

/** One line saying who a level lets in, shown next to the choice. */
export const CONFIDENTIALITY_BLURB: Record<Confidentiality, string> = {
  normal: 'Everyone with a login',
  restricted: 'Admins, the creator and named people',
  secret: 'Super admins, the creator and named people',
};

/** One person who can see a record, and why. */
export interface AccessEntry {
  userId: string;
  email: string;
  name: string | null;
  role: UserRole;
  via: 'role' | 'creator' | 'grant';
  reason?: string | null;
  grantedAt?: string | null;
  grantedBy?: string | null;
}

export interface AccessSummary {
  entityType: GrantEntityType;
  entityId: string;
  confidentiality: Confidentiality;
  confidentialityNote: string | null;
  openToEveryone: boolean;
  entries: AccessEntry[];
  canManage: boolean;
}

export interface AccessCandidate {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
}

/** Anything the API returns that carries a level. */
export interface ConfidentialRecord {
  confidentiality?: Confidentiality | null;
  confidentialityNote?: string | null;
}

export const levelOf = (record: ConfidentialRecord | null | undefined): Confidentiality =>
  record?.confidentiality ?? 'normal';

export const isHeldBack = (record: ConfidentialRecord | null | undefined): boolean =>
  levelOf(record) !== 'normal';

/**
 * Who may change a level, mirroring `canSetConfidentiality` on the server.
 * The server is the authority; this only decides whether to offer the control.
 */
export function canSetConfidentiality(
  role: UserRole | undefined,
  from: Confidentiality,
  to: Confidentiality,
): boolean {
  if (role === 'super_admin') return true;
  if (role !== 'admin') return false;
  return from !== 'secret' && to !== 'secret';
}
